import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBagIcon } from 'lucide-react';
import { Wordmark } from '../brand/Wordmark';
import { useCart } from '../../contexts/CartContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { MenuButton } from './MenuButton';
import { ShopNowButton } from '../shop/ShopNowButton';

/**
 * Minimal header: logo · language · cart · SHOP NOW · hamburger.
 * On the homepage the hero artwork carries the logo and menu, so this
 * header stays hidden until the hero has scrolled out of view.
 */
export function Header() {
  const {
    totals
  } = useCart();
  const {
    t,
    lang
  } = useLanguage();
  const {
    pathname
  } = useLocation();
  const isHome = pathname === '/';
  const [pastHero, setPastHero] = useState(!isHome);
  useEffect(() => {
    if (!isHome) {
      setPastHero(true);
      return;
    }
    setPastHero(false);
    const hero = document.getElementById('home-hero');
    if (!hero) return;
    const io = new IntersectionObserver(([entry]) => setPastHero(!entry.isIntersecting), {
      rootMargin: '-72px 0px 0px 0px'
    });
    io.observe(hero);
    return () => io.disconnect();
  }, [isHome]);
  const visible = !isHome || pastHero;
  const position = isHome ? `fixed inset-x-0 top-0 transition-[transform,visibility] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] ${visible ? 'visible translate-y-0' : 'invisible -translate-y-full'}` : 'sticky top-0';
  return <header className={`${position} z-40`} data-id="element-459" data-mp="3p2cpve2spa6b">
      <div className="border-b mf-hairline bg-deep/90 backdrop-blur-md" data-id="element-460" data-mp="3p34o6wns1m0a">
        <div className="mx-auto flex h-[72px] max-w-site items-center justify-between gap-4 px-5 md:px-8" data-id="element-461" data-mp="3tytgqg1dq2n3">
          <Link to="/" aria-label="MarineForceLED home" className="shrink-0" data-id="element-462" data-mp="1xwyb9pvdt3u">
            <Wordmark data-id="element-463" data-mp="199du7x971039" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3" data-id="element-464" data-mp="19ci2p6toyhzq">
            <nav aria-label="Customer service" className="mr-2 hidden items-center gap-6 lg:flex" data-id="element-465" data-mp="1dafxcp68dwai">
              <Link to="/#reviews" className="whitespace-nowrap text-[13px] font-medium uppercase tracking-[0.1em] text-titanium hover:text-white" data-id="element-466" data-mp="3ab29xjydd48t">
                {lang === 'es' ? 'Reseñas' : 'Reviews'}
              </Link>
              <Link to="/order-tracking" className="whitespace-nowrap text-[13px] font-medium uppercase tracking-[0.1em] text-titanium hover:text-white" data-id="element-467" data-mp="3jayyzmoo854e">
                {t('nav.track')}
              </Link>
              <Link to="/support" className="whitespace-nowrap text-[13px] font-medium uppercase tracking-[0.1em] text-titanium hover:text-white" data-id="element-468" data-mp="2qm1h3kodgac6">
                {t('nav.support')}
              </Link>
            </nav>
            <Link to="/cart" aria-label={`Cart, ${totals.lights} lights`} className="relative flex h-11 w-11 items-center justify-center rounded-md text-titanium transition-colors duration-150 hover:bg-white/5 hover:text-white" data-id="element-469" data-mp="2vnqu35v5my2w">
              <ShoppingBagIcon className="h-5 w-5" data-id="element-470" data-mp="igsnbb1fu6kh" />
              {totals.lights > 0 && <span className="absolute right-0 top-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-marine px-1 text-[11px] font-semibold text-white" data-id="element-471" data-mp="iexyhln1pub4">
                  {totals.lights}
                </span>}
            </Link>
            <ShopNowButton size="sm" source="header" showPrice className="hidden md:inline-flex" data-id="element-472" data-mp="efz0kkzqext6" />
            <MenuButton data-id="element-473" data-mp="12630oidpofff" />
          </div>
        </div>
      </div>
    </header>;
}